import {
  v2_getProductTitles,
  v2_isConditionallyAvailable,
  v2_isSingleChoiceProperty,
  v2_isUnavailableForDelivery,
  v2_isUnavailableForPickup,
  v2_ProductDetail,
  v2_selectMultipleChoiceConfigurationPropertyValue,
  v2_selectSingleChoiceConfigurationPropertyValue,
  v2_useAddConfigurationToCart,
  v2_useCart,
  v2_useProduct,
  v2_useProductList,
} from '@crocodille/common';
import { iife, isNone, isSome, Maybe } from '@crocodille/util';
import { useRouter } from '@framework/router';
import { formatCurrency } from '@utils/format';
import cn from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from '../../../hooks/use-media';
import { useAppLayoutState } from '../../../layouts/app-layout/app-layout';
import Button from '../../elements/button/button';
import CircleButton from '../../elements/circle-button/circle-button';
import Image from '../../elements/image/image';
import Tab from '../../elements/tab/tab';
import HorizontalListSelect from '../../horizontal-list-select/horizontal-list-select';
import ArrowIcon from '../../icons/arrow-icon';
import ShareIcon from '../../icons/share-icon';
import SpinnerIcon from '../../icons/spinner-icon';
import RadioSelect from '../../radio-select/radio-select';
import ConfigurableProduct from '../configurable-product/configurable-product';
import MenuIncentive from '../menu-incentive/menu-incentive';
import ProductOnlyDelivery from '../product-only-delivery/product-only-delivery';
import ProductOnlyPickup from '../product-only-pickup/product-only-pickup';
import ProductOutOfStock from '../product-out-of-stock/product-out-of-stock';
import ProductSpecialOffer from '../product-special-offer/product-special-offer';
import ProductTags from '../product-tags/product-tags';
import NutritionalValuesTable from '../product-nutritional-values/product-nutritional-values';
import * as routing from '@routing';
import Error from '@components/elements/error/error';
import PenIcon from '@components/icons/pen-icon';
import ProductIngredients from '../product-ingredients/product-ingredients';

import styles from './product-detail.module.scss';
import { isMenuUnavailable } from '@crocodille/common/lib/modules/products.v2';
import { getAddToCartError } from '@crocodille/bbdomu-i18n';

export interface ProductDetailProps {
  readonly slug: string;
  readonly taxon: string;
  readonly showPrices: boolean;
}

const CustomMenuConfiguration: React.FC<{
  readonly configuration: v2_ProductDetail['configuration'];
  readonly configurationValues: ReturnType<
    typeof v2_useProduct
  >['configurationValues'];
  readonly showPrices: boolean;
  setSingleChoiceProperty(propertyId: string, optionId: string | null): void;
}> = ({
  configuration,
  showPrices,
  configurationValues,
  setSingleChoiceProperty,
}) => {
  const [expandedProperty, setExpandedProperty] = useState<Maybe<string>>(
    configuration.find((p) => p.role === 'custom_menu')?.id,
  );

  const expandedPropertyRef = useRef(expandedProperty);
  expandedPropertyRef.current = expandedProperty;

  const customMenuProperties = configuration
    .filter((p) => p.role === 'custom_menu')
    .filter((p) => !p.hidden);
  const customMenuPropertiesRef = useRef(customMenuProperties);
  customMenuPropertiesRef.current = customMenuProperties;

  const advanceAccordion = useCallback(() => {
    if (customMenuPropertiesRef.current.length < 1) {
      return;
    }

    const currentIndex = customMenuPropertiesRef.current.findIndex(
      (item) => item.id === expandedPropertyRef.current,
    );
    const nextIndex =
      (currentIndex + 1) % customMenuPropertiesRef.current.length;

    if (nextIndex !== 0) {
      setExpandedProperty(customMenuPropertiesRef.current[nextIndex].id);
    }
  }, []);

  const { t } = useTranslation();

  return (
    <div className={styles.pastryWrapper}>
      <h3 className={styles.configuratorTitle}>
        {t('product.configure_custom_menu')}
      </h3>
      <Tab.Group
        selectedIndex={customMenuProperties.findIndex(
          (property) => property.id === expandedProperty,
        )}
        onChange={(index) =>
          setExpandedProperty(customMenuProperties?.[index]?.id)
        }
      >
        <Tab.List>
          {customMenuProperties.map((property) => (
            <Tab key={property.id}>{property.name}</Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {customMenuProperties.map((property) => (
            <Tab.Panel key={property.id}>
              <SingleChoicePropertyConfiguration
                property={property}
                value={v2_selectSingleChoiceConfigurationPropertyValue(
                  configurationValues,
                  property.id,
                )}
                onChange={(propertyId, optionId) => {
                  setSingleChoiceProperty(propertyId, optionId);
                  advanceAccordion();
                }}
                showPrices={showPrices}
              />
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
};

const ProductInfo: React.FC<
  React.PropsWithChildren<{
    readonly product: v2_ProductDetail;
    readonly isOnlyPickup: boolean;
    readonly isOnlyDelivery: boolean;
    readonly isMenuAvailable: boolean;
    readonly isModifyingIngredients: boolean;
    readonly onIngredientsModify: () => void;
  }>
> = ({
  children,
  product,
  isOnlyPickup,
  isOnlyDelivery,
  isMenuAvailable,
  isModifyingIngredients,
  onIngredientsModify,
}) => {
  const { t } = useTranslation();

  const canShareProduct =
    typeof window !== 'undefined' &&
    'share' in navigator &&
    navigator.canShare();

  const shareProduct = useCallback(() => {
    if (isSome(product)) {
      navigator.share({
        title: product.title,
        url: window.location.href,
      });
    }
  }, [product]);

  return (
    <div className={styles.contentHeader}>
      <div className={styles.cover}>
        <Image
          src={product.image}
          layout="fill"
          objectFit="cover"
          alt={product.title}
        />
        <ProductOnlyPickup isOnlyPickup={isOnlyPickup} />
        <ProductOnlyDelivery isOnlyDelivery={isOnlyDelivery} />
        <ProductSpecialOffer
          isSpecialItem={product.tags?.some((t) => t.id === 'daily_baguete')}
        />
        <ProductOutOfStock isAvailable={product.available && isMenuAvailable} />
        <ProductTags tags={product.tags?.map((t) => t.id) ?? []} inset />
      </div>
      <div className={styles.productMainInfo}>
        <div className={styles.productName}>
          <h2>{product.title} </h2>
        </div>
        {isSome(
          product.configuration.find((p) => p.role === 'remove_ingredients'),
        ) ? (
          <Button
            variant="basic"
            className={styles.ingredientsButton}
            onClick={onIngredientsModify}
          >
            <Button.Icon placement="left">
              <PenIcon />
            </Button.Icon>
            {isModifyingIngredients
              ? t('product.ingredients.back')
              : t('product.ingredients.modify')}
          </Button>
        ) : null}
        <p
          className={cn(styles.productComposition, {
            [styles.hidden]: isModifyingIngredients,
          })}
        >
          {product.description ?? ''}
        </p>
        {product.allergens && product.allergens.length > 0 ? (
          <p
            className={cn(styles.productAllergens, {
              [styles.hidden]: isModifyingIngredients,
            })}
          >
            <span className={styles.productAllergensTitle}>
              {t('product.allergens')}:&nbsp;
            </span>

            <span className={styles.productAllergens}>{product.allergens}</span>
          </p>
        ) : null}
        <div
          className={cn(styles.buttonBar, {
            [styles.hidden]: isModifyingIngredients,
          })}
        >
          {canShareProduct ? (
            <CircleButton
              label={t('product.share')}
              inverse={true}
              onClick={shareProduct}
            >
              <ShareIcon />
            </CircleButton>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
};

const SingleChoicePropertyConfiguration: React.FC<{
  readonly property: v2_ProductDetail['configuration'][number];
  readonly value: string | null;
  onChange(propertyId: string, optionId: string | null): void;
  readonly showPrices: boolean;
}> = ({ property, value, onChange, showPrices }) => {
  const { t } = useTranslation();

  const isHorizontalListMenu = useMedia(
    'menu-configuration-as-horizontal-list',
    false,
  );

  if (property.hidden) {
    return null;
  }

  const title = iife(() => {
    switch (property.role) {
      case 'pastry': {
        return t('product.pick_pastry');
      }
      case 'custom_menu': {
        return null;
      }
      default:
        return property.name ?? '';
    }
  });

  return (
    <>
      {isSome(title) ? <h3 className={styles.pastryTitle}>{title}</h3> : null}
      {isHorizontalListMenu ? (
        <HorizontalListSelect
          value={value}
          onChange={(v) => onChange(property.id, v)}
          className={cn({ [styles.listLarge]: property.options.length > 4 })}
        >
          {property.options.map((option) => (
            <HorizontalListSelect.Option
              key={option.itemCode}
              value={option.itemCode}
              label={option.name}
              price={showPrices ? option.unitPrice ?? undefined : undefined}
              image={option.image}
              disabled={!option.available}
            >
              <ProductOutOfStock isAvailable={option.available} forceMobile />
            </HorizontalListSelect.Option>
          ))}
        </HorizontalListSelect>
      ) : (
        <RadioSelect value={value} onChange={(v) => onChange(property.id, v)}>
          {property.options.map((option) => (
            <RadioSelect.Option
              key={option.itemCode}
              value={option.itemCode}
              label={option.name}
              price={showPrices ? option.unitPrice : null}
              image={option.image}
              rotateImage={
                property.role === 'pastry' || property.hint === 'pastry'
              }
              disabled={!option.available}
            >
              <ProductOutOfStock isAvailable={option.available} forceMobile />
            </RadioSelect.Option>
          ))}
        </RadioSelect>
      )}
    </>
  );
};

const ProductDetailV2: React.FC<
  React.PropsWithChildren<ProductDetailProps>
> = ({ slug, taxon, showPrices = true }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { push } = useRouter();

  const isGiftCart = taxon === 'darkove-karty';

  const {
    data: product,
    configurationValues,
    filteredConfiguration,
    clearConfiguration,
    setSingleChoiceProperty,
    setMultipleChoiceProperty,
    optionsNutritionValues,
  } = v2_useProduct(slug);

  const {
    totalPrice,
    configurationValidationErrors,
    canAddToCart,
    addConfigurationToCart,
    isMutating: addingToCart,
    error: addingToCartError,
  } = v2_useAddConfigurationToCart({
    product,
    filteredConfiguration,
    configurationValues,
  });

  let menuComposition = '';
  let removedIngredientsComposition = '';

  if (filteredConfiguration) {
    menuComposition = filteredConfiguration
      .filter((p) => !p.hidden)
      .filter((p) => p.role !== 'menu_incentive')
      .map((p) => {
        if (v2_isSingleChoiceProperty(p)) {
          const value = v2_selectSingleChoiceConfigurationPropertyValue(
            configurationValues,
            p.id,
          );

          const option = p.options.find((o) => o.itemCode === value);

          return option?.name;
        } else {
          const values = v2_selectMultipleChoiceConfigurationPropertyValue(
            configurationValues,
            p.id,
          );

          if (isNone(values) || Object.keys(values).length === 0) {
            return null;
          }

          if (p.role === 'remove_ingredients') {
            return null;
          }

          return Object.entries(values)
            .map(([code, count = 0]) => {
              const option = p.options.find((o) => o.itemCode === code);

              if (isNone(option)) {
                return null;
              }

              if (count > 1) {
                return `${option.name} ${count}x`;
              } else if (count >= 1) {
                return option.name;
              }
            })
            .filter(isSome)
            .join(', ');
        }
      })
      .filter(isSome)
      .join(', ');
    removedIngredientsComposition = filteredConfiguration
      .filter((p) => p.role === 'remove_ingredients')
      .map((p) => {
        const values = v2_selectMultipleChoiceConfigurationPropertyValue(
          configurationValues,
          p.id,
        );

        if (isNone(values) || Object.keys(values).length === 0) {
          return null;
        }

        return Object.entries(values)
          .map(([code, count = 1]) => {
            const option = p.options.find((o) => o.itemCode === code);

            if (isNone(option)) {
              return null;
            }

            if (count === 0) {
              return option.name;
            }
          })
          .filter(isSome)
          .join(', ');
      })
      .filter(isSome)
      .join(', ');
  }

  const { toggleCartOpen } = useAppLayoutState();

  const configurationContainerRef = useRef<HTMLDivElement>(null);

  const canAddToCartRef = useRef(canAddToCart);
  canAddToCartRef.current = canAddToCart;

  const [offerMenuIncentive, setOfferMenuIncentive] = useState(false);
  const [productNutritionValues, setProductNutritionValues] = useState(false);
  const [productIngredientsModify, setProductIngredientsModify] =
    useState(false);

  const menuIncentive = filteredConfiguration?.find(
    (c) => c.role === 'menu_incentive',
  );
  const menuIncentiveValue = menuIncentive
    ? v2_selectSingleChoiceConfigurationPropertyValue(
        configurationValues,
        menuIncentive.id,
      )
    : undefined;

  const menuIncentiveValueRef = useRef(menuIncentiveValue);
  menuIncentiveValueRef.current = menuIncentiveValue;

  const onlyMenuIncentiveMissing =
    menuIncentive &&
    configurationValidationErrors.every(
      (e) => e.propertyId === menuIncentive.id,
    );

  const onlyMenuIncentiveMissingRef = useRef(onlyMenuIncentiveMissing);
  onlyMenuIncentiveMissingRef.current = onlyMenuIncentiveMissing;

  const hasCalledRef = useRef(false);

  const handleConfiguratorAnimation = () => {
    configurationContainerRef.current?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center',
    });

    const tabPanels = Array.from(
      configurationContainerRef?.current?.querySelectorAll(
        '[role="tabpanel"]',
      ) ?? [],
    );

    const highlight =
      tabPanels.length > 0
        ? tabPanels
        : [configurationContainerRef?.current].filter(isSome);

    for (const el of highlight) {
      el.animate(
        [
          {
            background: 'var(--color-brand-1)',
          },
          {
            background: 'var(--color-brand-1-light)',
          },
        ],
        {
          duration: 600,
          easing: 'ease-out',
          iterations: 1,
        },
      );
    }

    const configuratorInvalidGroup =
      typeof window !== 'undefined'
        ? document.querySelector('[data-invalid-configurator-group="true"]')
        : null;

    if (configuratorInvalidGroup) {
      configuratorInvalidGroup.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const handleAddToCart = useCallback(
    async (skipMenuIncentive = false) => {
      if (
        !skipMenuIncentive &&
        menuIncentive &&
        isNone(menuIncentiveValueRef.current) &&
        onlyMenuIncentiveMissingRef.current
      ) {
        setOfferMenuIncentive(true);
        return;
      }

      if (canAddToCartRef.current) {
        const added = await addConfigurationToCart();
        if (added) {
          clearConfiguration();
          await push(routing.products({ taxon }), undefined, {
            scroll: false,
          });
          toggleCartOpen(true);
        }
      } else {
        handleConfiguratorAnimation();
      }
    },
    [
      menuIncentive,
      addConfigurationToCart,
      clearConfiguration,
      push,
      taxon,
      toggleCartOpen,
    ],
  );

  useEffect(() => {
    const shouldTrigger =
      !addingToCart &&
      menuIncentive &&
      menuIncentiveValue &&
      !hasCalledRef.current;

    if (shouldTrigger) {
      hasCalledRef.current = true;
      handleAddToCart(true);
    }
  }, [addingToCart, menuIncentive, menuIncentiveValue, handleAddToCart]);

  useEffect(() => {
    if (!menuIncentive || !menuIncentiveValue) {
      hasCalledRef.current = false;
    }
  }, [menuIncentive, menuIncentiveValue]);

  const { data: cart } = v2_useCart();
  const { data: products } = v2_useProductList();

  if (isNone(product)) {
    return null;
  }

  const onNutritionalButtonClick = () => {
    if (
      isSome(product.configurationStyle) &&
      product.configurationStyle !== 'incentive_only' &&
      filteredConfiguration.some((o) => !o.hidden) &&
      !menuComposition
    ) {
      handleConfiguratorAnimation();
    } else {
      setProductNutritionValues(true);
    }
  };

  const onIngredientsModifyClick = () => {
    if (
      isSome(product.configurationStyle) &&
      product.configurationStyle !== 'incentive_only' &&
      filteredConfiguration.some((o) => !o.hidden) &&
      !menuComposition
    ) {
      handleConfiguratorAnimation();
    } else {
      if (productIngredientsModify) {
        filteredConfiguration
          .filter((p) => p.role === 'remove_ingredients')
          .map((p) => {
            const values = v2_selectMultipleChoiceConfigurationPropertyValue(
              configurationValues,
              p.id,
            );

            if (isNone(values) || Object.keys(values).length === 0) {
              return null;
            }

            Object.entries(values).map(([code]) => {
              const option = p.options.find((o) => o.itemCode === code);

              if (isNone(option)) {
                return null;
              }

              setMultipleChoiceProperty(
                'remove_ingredients',
                option.itemCode,
                1,
              );
            });
          });
        setProductIngredientsModify(false);
      } else {
        setProductIngredientsModify(true);
      }
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.contentInner}>
            {product.configurationStyle === 'configurator' ? (
              <ConfigurableProduct
                product={product}
                configurationValues={configurationValues}
                setMultipleChoiceProperty={setMultipleChoiceProperty}
                setSingleChoiceProperty={setSingleChoiceProperty}
              >
                <div
                  className={cn(styles.nutritionalButton, styles.configurator)}
                >
                  <Button
                    variant="basic"
                    onClick={() => setProductNutritionValues(true)}
                  >
                    {t('product.nutritional_values.show')}
                  </Button>
                </div>
              </ConfigurableProduct>
            ) : (
              <ProductInfo
                isOnlyPickup={v2_isUnavailableForDelivery(product, cart)}
                isOnlyDelivery={v2_isUnavailableForPickup(product, cart)}
                product={product}
                isMenuAvailable={
                  !isMenuUnavailable(product, filteredConfiguration)
                }
                isModifyingIngredients={productIngredientsModify}
                onIngredientsModify={onIngredientsModifyClick}
              >
                {!isGiftCart ? (
                  <div
                    className={cn(styles.nutritionalButton, {
                      [styles.hidden]: productIngredientsModify,
                    })}
                  >
                    <Button variant="basic" onClick={onNutritionalButtonClick}>
                      {t('product.nutritional_values.show')}
                    </Button>
                  </div>
                ) : null}
              </ProductInfo>
            )}
            {isSome(product.configurationStyle) && !productIngredientsModify ? (
              <div
                ref={configurationContainerRef}
                className={styles.pastryContainer}
              >
                {product.configurationStyle === 'custom_menu' &&
                filteredConfiguration.some((p) => !p.hidden) ? (
                  <CustomMenuConfiguration
                    configuration={filteredConfiguration}
                    configurationValues={configurationValues}
                    showPrices={showPrices}
                    setSingleChoiceProperty={setSingleChoiceProperty}
                  />
                ) : (
                  <>
                    {product.configuration
                      .filter(
                        (p) =>
                          p.role !== 'menu_incentive' &&
                          p.role !== 'ingredient',
                      )
                      .map((property) => {
                        if (v2_isSingleChoiceProperty(property)) {
                          return (
                            <SingleChoicePropertyConfiguration
                              key={property.id}
                              property={property}
                              value={v2_selectSingleChoiceConfigurationPropertyValue(
                                configurationValues,
                                property.id,
                              )}
                              onChange={setSingleChoiceProperty}
                              showPrices={showPrices}
                            />
                          );
                        } else {
                          return null;
                        }
                      })
                      .filter(isSome)}
                  </>
                )}
              </div>
            ) : null}
            {productIngredientsModify ? (
              <ProductIngredients
                product={product}
                setMultipleChoiceProperty={setMultipleChoiceProperty}
                configurationValues={configurationValues}
              />
            ) : null}
            {product.configurationStyle !== 'configurator' &&
            !isGiftCart &&
            !productIngredientsModify ? (
              <div className={cn(styles.nutritionalButton, styles.mobile)}>
                <Button variant="basic" onClick={onNutritionalButtonClick}>
                  {t('product.nutritional_values.show')}
                </Button>
              </div>
            ) : null}
            <div className={styles.cartControls}>
              {addingToCartError ? (
                <Error className={styles.cartError}>
                  {t(getAddToCartError(addingToCartError.kind), {
                    products:
                      addingToCartError.kind === 'product_unavailable' &&
                      products
                        ? ` (${v2_getProductTitles(
                            products,
                            addingToCartError.productVariantCodes,
                          ).join(', ')})`
                        : null,
                  })}
                </Error>
              ) : null}
              <div className={styles.menuComposition}>
                {removedIngredientsComposition ? (
                  <p>
                    {t('product.ingredients.menu_composition', {
                      product: product.title,
                      ingredients: removedIngredientsComposition,
                    })}
                  </p>
                ) : null}
                {!canAddToCart &&
                !onlyMenuIncentiveMissing &&
                product.configurationStyle
                  ? product.configurationStyle === 'custom_menu'
                    ? t('product.configure_custom_menu')
                    : product.configurationStyle === 'configurator'
                    ? t('product.configure_configurator')
                    : t('product.configure_pastry')
                  : menuComposition}
              </div>

              <div className={styles.totalPrice}>
                {showPrices && isSome(totalPrice)
                  ? formatCurrency(totalPrice, 'CZK', language)
                  : ''}
              </div>

              <Button
                className={cn(styles.addToCart)}
                onClick={() => handleAddToCart()}
                size="large"
                symmetrical={false}
                disabled={
                  !v2_isConditionallyAvailable(product, cart) ||
                  isMenuUnavailable(product, filteredConfiguration)
                }
              >
                {!canAddToCart &&
                !onlyMenuIncentiveMissing &&
                product.configurationStyle ? (
                  product.configurationStyle === 'custom_menu' ? (
                    t('product.configure_custom_menu')
                  ) : product.configurationStyle === 'configurator' ? (
                    t('product.configure_configurator')
                  ) : (
                    t('product.configure_pastry')
                  )
                ) : (
                  <>
                    <span className={styles.visibleDesktop}>
                      {t('product.add_to_cart_desktop')}
                    </span>
                    <span className={styles.visibleMobile}>
                      {t('product.add_to_cart')}
                    </span>
                  </>
                )}

                <Button.Icon>
                  {addingToCart ? (
                    <SpinnerIcon />
                  ) : (
                    <ArrowIcon
                      className={cn({
                        [styles.isEmphasized]: !canAddToCart,
                      })}
                    />
                  )}
                </Button.Icon>
              </Button>
            </div>
          </div>
        </div>
      </div>
      <MenuIncentive
        open={offerMenuIncentive}
        incentive={menuIncentive}
        incentiveValue={menuIncentiveValue}
        showPrices={isSome(cart)}
        onChoice={(code) => {
          if (menuIncentive) {
            setSingleChoiceProperty(menuIncentive.id, code);
          }

          setOfferMenuIncentive(false);
        }}
        onClose={() => setOfferMenuIncentive(false)}
      />

      <NutritionalValuesTable
        product={product}
        open={productNutritionValues}
        onClose={() => setProductNutritionValues(false)}
        configurationValue={menuComposition}
        optionsNutritionValues={optionsNutritionValues}
      />
    </>
  );
};

export default ProductDetailV2;
